import { useSyncExternalStore } from 'react';

/** The languages every sample on the developer pages is written in. */
export const languages = [
  { value: 'curl', label: 'curl' },
  { value: 'python', label: 'Python' },
  { value: 'csharp', label: 'C#' },
  { value: 'javascript', label: 'JavaScript' },
] as const;

export type Language = (typeof languages)[number]['value'];

const storageKey = 'paige.developers.language';
const listeners = new Set<() => void>();
let current: Language = remembered();

function isLanguage(value: unknown): value is Language {
  return languages.some((l) => l.value === value);
}

/** The reader's last choice, when the browser kept it; storage may be off or empty. */
function remembered(): Language {
  try {
    const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(storageKey);
    return isLanguage(stored) ? stored : 'curl';
  } catch {
    return 'curl';
  }
}

/** Choose a language for every sample on the page at once, and remember it for the next visit. */
export function chooseLanguage(value: string): void {
  if (!isLanguage(value) || value === current) return;
  current = value;
  try {
    localStorage.setItem(storageKey, value);
  } catch {
    // A convenience only: the choice still holds for this visit.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The chosen language; every sample re-renders when it changes. */
export function useLanguage(): Language {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => 'curl' as Language,
  );
}
